import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

import { useHuntStore } from '@/stores/hunt-store';
import { usePropertyStore } from '@/stores/property-store';
import { Button } from '@/components/common/Button';
import { DateField } from '@/components/forms/DateField';
import { useMediaQuery } from '@/hooks/use-media-query';

// M-08 (Defect Batch M): inward outline so focus ring is never clipped (ADR-035).
const INPUT_CLASS =
  'w-full min-w-0 box-border px-3 py-2.5 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-placeholder)] focus:outline-2 focus:outline-[var(--color-accent)] focus:outline-offset-[-2px]';

function FieldLabel({
  htmlFor,
  children,
  required,
}: {
  htmlFor?: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="field-label"
    >
      {children}
      {required && <span className="text-[var(--color-accent)] ml-1">*</span>}
    </label>
  );
}

function FieldWrapper({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-1.5">{children}</div>;
}

export function HuntEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { currentHunt, isLoading, loadHunt, updateHunt, deleteHunt } = useHuntStore();
  const { properties } = usePropertyStore();
  // J-05: viewport breakpoint — "side-by-side" at sm (640px+), "stacked" below
  const isWide = useMediaQuery('(min-width: 640px)');

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [companionInput, setCompanionInput] = useState('');
  const [companions, setCompanions] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (id) void loadHunt(id);
  }, [id, loadHunt]);

  // Populate form when hunt loads
  useEffect(() => {
    if (!currentHunt) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
    setName(currentHunt.name);
    setStartDate(currentHunt.startDate);
    setEndDate(currentHunt.endDate);
    setPropertyId(currentHunt.propertyId);
    setCompanions(currentHunt.companions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHunt?.id]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs['name'] = 'Name is required';
    if (!startDate) errs['startDate'] = 'Start date is required';
    if (!endDate) errs['endDate'] = 'End date is required';
    if (startDate && endDate && endDate < startDate)
      errs['endDate'] = 'End date must be on or after start date';
    if (!propertyId) errs['propertyId'] = 'Property is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate() || !id) return;
    setIsSaving(true);
    try {
      await updateHunt(id, {
        name: name.trim(),
        startDate,
        endDate,
        propertyId,
        companions,
      });
      navigate(`/hunt/${id}`);
    } catch {
      setIsSaving(false);
    }
  }

  const handleDelete = useCallback(async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await deleteHunt(id);
      navigate('/hunt');
    } catch (err) {
      console.error('[HuntEditPage] Failed to delete hunt:', err);
      setIsDeleting(false);
    }
  }, [id, deleteHunt, navigate]);

  const addCompanion = useCallback(() => {
    const trimmed = companionInput.trim();
    if (!trimmed || companions.length >= 8) return;
    if (!companions.includes(trimmed)) {
      setCompanions((prev) => [...prev, trimmed]);
    }
    setCompanionInput('');
  }, [companionInput, companions]);

  const removeCompanion = useCallback((name: string) => {
    setCompanions((prev) => prev.filter((c) => c !== name));
  }, []);

  function handleCompanionKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCompanion();
    }
  }

  if (isLoading || !currentHunt) {
    return (
      <div className="flex flex-col gap-4 animate-pulse">
        <div className="h-8 bg-[var(--color-elevated)] rounded w-1/2" />
        <div className="h-12 bg-[var(--color-elevated)] rounded" />
        <div className="h-12 bg-[var(--color-elevated)] rounded" />
        <div className="h-12 bg-[var(--color-elevated)] rounded" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 overflow-hidden" data-testid="hunt-edit-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to={`/hunt/${id ?? ''}`}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          aria-label="Back"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
          Edit Hunt Trip
        </h1>
      </div>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 overflow-hidden"
        data-testid="hunt-edit-form"
      >
        {/* Name */}
        <FieldWrapper>
          <FieldLabel htmlFor="hunt-name" required>
            Trip Name
          </FieldLabel>
          <input
            id="hunt-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Thanksgiving weekend"
            className={INPUT_CLASS}
            data-testid="input-name"
          />
          {errors['name'] && (
            <p className="text-body-xs text-[var(--color-accent)]">{errors['name']}</p>
          )}
        </FieldWrapper>

        {/* Date range — grid-cols-1 on mobile, 2 on sm+ (J-05) */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0"
          data-date-layout={isWide ? 'side-by-side' : 'stacked'}
        >
          <div className="min-w-0">
            <DateField
              id="hunt-start"
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              required
            />
            {errors['startDate'] && (
              <p className="text-body-xs text-[var(--color-accent)] mt-1">{errors['startDate']}</p>
            )}
          </div>

          <div className="min-w-0">
            <DateField
              id="hunt-end"
              label="End Date"
              value={endDate}
              min={startDate}
              onChange={setEndDate}
              required
            />
            {errors['endDate'] && (
              <p className="text-body-xs text-[var(--color-accent)] mt-1" data-testid="error-end-date">
                {errors['endDate']}
              </p>
            )}
          </div>
        </div>

        {/* Property */}
        <FieldWrapper>
          <FieldLabel htmlFor="hunt-property" required>
            Property
          </FieldLabel>
          <select
            id="hunt-property"
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            className={INPUT_CLASS}
            data-testid="select-property"
          >
            <option value="">— Select property —</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {errors['propertyId'] && (
            <p className="text-body-xs text-[var(--color-accent)]">{errors['propertyId']}</p>
          )}
        </FieldWrapper>

        {/* Companions */}
        <FieldWrapper>
          <FieldLabel>Companions</FieldLabel>
          {companions.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-1" data-testid="companion-chips">
              {companions.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-body-xs font-medium bg-[var(--color-elevated)] text-[var(--color-text-secondary)] border border-[var(--color-border)]"
                >
                  {c}
                  <button
                    type="button"
                    onClick={() => removeCompanion(c)}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] leading-none"
                    aria-label={`Remove ${c}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={companionInput}
              onChange={(e) => setCompanionInput(e.target.value)}
              onKeyDown={handleCompanionKeyDown}
              placeholder="Add a name…"
              maxLength={50}
              disabled={companions.length >= 8}
              className={`${INPUT_CLASS} flex-1`}
              data-testid="input-companion"
            />
            <button
              type="button"
              onClick={addCompanion}
              disabled={!companionInput.trim() || companions.length >= 8}
              className="px-3 py-2.5 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="add-companion-btn"
            >
              Add
            </button>
          </div>
        </FieldWrapper>

        {/* Actions */}
        <div className="flex gap-3 mt-2">
          <Button
            type="button"
            variant="ghost"
            className="flex-1"
            onClick={() => navigate(`/hunt/${id ?? ''}`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSaving}
            className="flex-1"
            data-testid="submit-button"
          >
            {isSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </form>

      {/* Delete Hunt */}
      <div className="mt-4 pt-6 border-t border-[var(--color-border)]">
        {confirmDelete ? (
          <div
            className="flex flex-col gap-3 bg-red-950/20 border border-red-800/40 rounded-lg p-4"
            data-testid="delete-confirm"
          >
            <p className="text-body-sm text-[var(--color-text-primary)]">
              Delete this hunt trip? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="danger"
                disabled={isDeleting}
                onClick={() => void handleDelete()}
                data-testid="confirm-delete-btn"
              >
                {isDeleting ? 'Deleting…' : 'Yes, Delete Hunt'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="danger"
            onClick={() => setConfirmDelete(true)}
            data-testid="delete-hunt-btn"
          >
            Delete Hunt
          </Button>
        )}
      </div>
    </div>
  );
}
